import { useState, PropsWithChildren, useRef, Fragment } from 'react'

import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  inline,
  useInteractions,
  useHover,
  arrow,
  Placement,
  FloatingArrow,
} from '@floating-ui/react'

import { mergeClassName } from '@erudilabs/merge-class-utils'

export type TooltipPlacementProps = Placement

export interface TooltipProps extends PropsWithChildren {
  className?: string
  content: string
  testId?: string
  placement?: TooltipPlacementProps
}
export const Tooltip = (props: TooltipProps) => {
  const { children, content, className, testId, placement = 'top' } = props
  const arrowRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const ARROW_HEIGHT = 8
  const GAP = 2

  const { x, y, strategy, refs, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      inline(),
      offset(ARROW_HEIGHT + GAP),
      flip({
        fallbackAxisSideDirection: 'start',
        fallbackPlacements: [
          'top',
          'right',
          'bottom',
          'left',
          'top-start',
          'top-end',
          'right-start',
          'right-end',
          'bottom-start',
          'bottom-end',
          'left-start',
          'left-end',
        ],
      }),
      arrow({
        element: arrowRef,
      }),
    ],
    placement,
    whileElementsMounted: autoUpdate,
  })
  const hover = useHover(context, { move: false })
  const { getReferenceProps, getFloatingProps } = useInteractions([hover])

  return (
    <Fragment>
      <div
        data-testid={testId}
        ref={refs.setReference}
        {...getReferenceProps()}
        className={mergeClassName('cursor-pointer inline-block', className)}
      >
        {children}
      </div>
      {isOpen && (
        <div
          ref={refs.setFloating}
          className={mergeClassName([
            'block text-left absolute z-30 p-2 text-sm transition-opacity duration-300 rounded-lg shadow-sm max-w-[400px]',
            'bg-gray-800',
            'dark:bg-gray-200',
          ])}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            width: 'max-content',
          }}
          {...getFloatingProps()}
        >
          <p className="text-gray-50 dark:text-black">{content}</p>
          <FloatingArrow
            ref={arrowRef}
            context={context}
            className="fill-gray-800 dark:fill-gray-200"
          />
        </div>
      )}
    </Fragment>
  )
}
